import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { KTSVG } from '../../_metronic/helpers';
import CostCenterModal from '../components/CostCenterCreate';
import { deleteRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';

interface CostCenter {
  idCostCenters: number;
  name: string;
  details?: string | null;
  idProjects?: number | string | null;
  projectName?: string | null;
}

const CostCenterList: React.FC = () => {
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');

  const fetchCostCenters = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (searchText.trim() !== '') {
      params.append('search', searchText.trim());
    }
    const url = `${process.env.REACT_APP_API_URL}cost-centers${params.toString() ? `?${params.toString()}` : ''}`;
    fetch(url, getRequestOptions())
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.success) {
          setCostCenters(resp.data || []);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            html: resp?.errorMessage || 'Failed to load cost centers.',
          });
        }
      })
      .catch((error) => {
        console.log(error, 'fetch cost centers error');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          html: 'Something went wrong while fetching cost centers.',
        });
      })
      .finally(() => setLoading(false));
  }, [searchText]);

  useEffect(() => {
    fetchCostCenters();
  }, [fetchCostCenters]);

  const handleDelete = (costCenter: CostCenter) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${costCenter.name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      showLoaderOnConfirm: true,
      customClass: {
        confirmButton: 'btn btn-danger me-2',
        cancelButton: 'btn btn-secondary',
      },
      preConfirm: async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}cost-centers/${costCenter.idCostCenters}`,
            deleteRequestOptions()
          );

          const data = await response.json();

          if (!response.ok || !data.success) {
            const message =
              (Array.isArray(data?.errors) && data.errors.length > 0)
                ? data.errors.join('<br>')
                : data?.errorMessage || `Request failed with status ${response.status}`;
            Swal.showValidationMessage(message);
            return null;
          }

          return data;
        } catch (error: any) {
          const message = error?.message ?? 'Request failed';
          Swal.showValidationMessage(message);
          return null;
        }
      },
    }).then((result) => {
      if (result.isConfirmed && result.value?.success) {
        Swal.fire(
          'Deleted!',
          result.value?.successMessage || 'Cost center deleted successfully.',
          'success'
        );
        fetchCostCenters();
      }
    });
  };

  const handleCreate = () => {
    setSelectedCostCenter(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (costCenter: CostCenter) => {
    setSelectedCostCenter(costCenter);
    setIsModalOpen(true);
  };

  const handleModalClose = (isSuccess: boolean) => {
    setIsModalOpen(false);
    setSelectedCostCenter(undefined);
    if (isSuccess) {
      fetchCostCenters();
    }
  };

  return (
    <div className="container py-10">
      <div className="card card-bordered shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <div className='card-title'>
            <KTSVG
              path='/media/svg/icons/Text/Bullet-list.svg'
              className='svg-icon-1 svg-icon-primary'
            />
            <span className='text fw-bolder ms-3'>
              Cost Centers
            </span>
          </div>
          <div className="d-flex">
            <input
              type="text"
              className="form-control form-control-sm me-2"
              placeholder="Search by name, project, or details"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ minWidth: '280px' }}
            />
            <button className="btn btn-sm btn-light-primary me-2 px-3 d-flex align-items-center gap-1" onClick={fetchCostCenters}>
              <i className="fa fa-search"></i>
              <span>Search</span>
            </button>
            <button
              className="btn btn-sm btn-primary px-3 d-inline-flex align-items-center gap-1"
              style={{ whiteSpace: 'nowrap' }}
              onClick={handleCreate}
            >
              <i className="fa fa-plus"></i>
              <span>Add Cost Center</span>
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: '10%' }}>ID</th>
                  <th style={{ width: '25%' }}>Name</th>
                  <th style={{ width: '25%' }}>Project</th>
                  <th style={{ width: '20%' }}>Details</th>
                  <th style={{ width: '20%' }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5">
                      Loading...
                    </td>
                  </tr>
                ) : costCenters.length > 0 ? (
                  costCenters.map((center) => (
                    <tr key={center.idCostCenters}>
                      <td>{center.idCostCenters}</td>
                      <td>{center.name}</td>
                      <td>{center.projectName ?? '-'}</td>
                      <td>{center.details || <span className="text-muted">N/A</span>}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-light-primary btn-sm me-2"
                          onClick={() => handleEdit(center)}
                        >
                          <KTSVG
                            path='/media/svg/icons/Communication/Write.svg'
                            className='svg-icon-5 svg-icon'
                          /> Edit
                        </button>
                        <button
                          className="btn btn-light-danger btn-sm"
                          onClick={() => handleDelete(center)}
                        >
                          <KTSVG
                            path='/media/svg/icons/General/Trash.svg'
                            className='svg-icon-5 svg-icon'
                          /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-5">
                      No cost centers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CostCenterModal
        show={isModalOpen}
        handleClose={handleModalClose}
        initialData={selectedCostCenter}
      />
    </div>
  );
};

export default CostCenterList;

